import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/common/Button';
import { IdFieldSelect } from '../../components/formFields/IdFieldSelect';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { getFileCategories, type LookupItem } from '../../services/criminalCaseRequests';
import {
  createSubCategory,
  getSubCategoryById,
  listDocumentTypes,
  updateSubCategory,
  type DocumentTypeOption,
} from '../../services/caseRequestSubCategories';
import {
  buildSubCategoryCodeFromName,
  isValidSubCategoryCode,
} from '../../utils/subCategoryCodeGeneration';
import { setFlashToast } from '../../utils/flashToast';
import {
  canAddApplicableAreaRow,
  findDuplicateApplicableAreaIndices,
} from '../../utils/applicableAreaRowsValidation';
import { focusFirstInvalid, validateRequired, type RequiredErrors, type RequiredFieldSpec } from '../../utils/requiredValidation';
import { useAuth } from '../../contexts/AuthContext';

const READONLY_FIELD_STYLE = {
  background: 'color-mix(in srgb, var(--bg) 70%, var(--surface))',
  color: 'var(--t2)',
  cursor: 'not-allowed' as const,
};

export default function SubCategoryFormPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { subCategoryId } = useParams();
  const isEdit = Boolean(subCategoryId);
  const nav = useNavigate();

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [reqErrors, setReqErrors] = useState<RequiredErrors>({});

  const [fileCategories, setFileCategories] = useState<LookupItem[]>([]);
  const [documentTypes, setDocumentTypes] = useState<DocumentTypeOption[]>([]);
  const [lookupsState, setLookupsState] = useState<'idle' | 'loading' | 'ready'>('idle');

  const [categoryId, setCategoryId] = useState('');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [active, setActive] = useState(true);
  const [bindName, setBindName] = useState(true);
  const [applicableAreaRows, setApplicableAreaRows] = useState<string[]>(['']);
  const [selectedDocumentTypeIds, setSelectedDocumentTypeIds] = useState<string[]>([]);
  const [documentOptionalById, setDocumentOptionalById] = useState<Record<string, boolean>>({});

  const clearReqError = (key: string) => {
    setReqErrors((cur) => {
      if (!cur[key]) return cur;
      const next = { ...cur };
      delete next[key];
      return next;
    });
  };

  useEffect(() => {
    if (!user) return;
    let alive = true;
    setLookupsState('loading');
    void Promise.all([getFileCategories(), listDocumentTypes()]).then(([cats, docs]) => {
      if (!alive) return;
      setFileCategories(cats.success ? cats.data : []);
      setDocumentTypes(docs.success ? docs.data : []);
      setLookupsState('ready');
    });
    return () => {
      alive = false;
    };
  }, [user?.id]);

  useEffect(() => {
    if (!isEdit || !subCategoryId) return;
    let alive = true;
    setLoading(true);
    void getSubCategoryById(subCategoryId).then((res) => {
      if (!alive) return;
      if (res.success) {
        const d = res.data;
        setCategoryId(d.category?.id ?? '');
        setName(d.name);
        setCode(d.code);
        setActive(d.active);
        setApplicableAreaRows(
          d.applicableAreas.length > 0 ? d.applicableAreas.map((a) => a.name) : [''],
        );
        setSelectedDocumentTypeIds(d.checklistDocuments.map((doc) => doc.documentTypeId));
        setDocumentOptionalById(
          Object.fromEntries(d.checklistDocuments.map((doc) => [doc.documentTypeId, doc.optional])),
        );
      } else {
        setReqErrors({ form: res.message ?? t('admin.shared.requestFailed') });
      }
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [isEdit, subCategoryId, t]);

  useEffect(() => {
    if (isEdit) return;
    setCode(buildSubCategoryCodeFromName(name));
  }, [name, isEdit]);

  const requiredSpecs = useMemo<RequiredFieldSpec[]>(() => {
    const specs: RequiredFieldSpec[] = [];
    if (!isEdit) {
      specs.push({
        key: 'categoryId',
        label: t('admin.shared.fileCategory'),
        value: categoryId,
        elementId: 'subCategory-category',
      });
      specs.push({
        key: 'name',
        label: t('profile.name'),
        value: name,
        elementId: 'subCategory-name',
      });
    }
    return specs;
  }, [categoryId, isEdit, name, t]);

  const duplicateApplicableAreaIndices = useMemo(
    () => findDuplicateApplicableAreaIndices(applicableAreaRows),
    [applicableAreaRows],
  );
  const canAddApplicableArea = canAddApplicableAreaRow(applicableAreaRows);

  async function handleSave() {
    if (savingRef.current) return;

    const errs = validateRequired(requiredSpecs);
    if (!isEdit) {
      const resolvedCode = buildSubCategoryCodeFromName(name.trim());
      if (!isValidSubCategoryCode(resolvedCode)) {
        errs.code = t('metadata.subCategoryForm.codeLengthError');
      }
    }
    if (duplicateApplicableAreaIndices.size > 0) {
      errs.applicableAreas = t('metadata.subCategoryForm.applicableAreaDuplicateError');
    }
    setReqErrors(errs);
    if (Object.keys(errs).length) {
      if (errs.applicableAreas) {
        document.getElementById('subCategory-applicableAreas')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        focusFirstInvalid(requiredSpecs, errs);
      }
      return;
    }

    const applicableAreas = applicableAreaRows.map((row) => row.trim()).filter(Boolean);
    const documents = selectedDocumentTypeIds.map((documentId) => ({
      documentId,
      optional: documentOptionalById[documentId] ?? false,
    }));

    savingRef.current = true;
    setSaving(true);
    let keepLocked = false;
    try {
      if (isEdit && subCategoryId) {
        const res = await updateSubCategory(subCategoryId, {
          name: name.trim(),
          code: code.trim(),
          active,
          applicableAreas,
          documents,
        });
        if (!res.success) {
          setReqErrors({ form: res.message ?? t('admin.shared.requestFailed') });
          return;
        }
        setFlashToast({ message: t('metadata.subCategoryForm.updateSuccess'), variant: 'success' });
      } else {
        const resolvedCode = buildSubCategoryCodeFromName(name.trim());
        const res = await createSubCategory({
          name: name.trim(),
          code: resolvedCode,
          active,
          categoryId,
          codeAvailable: bindName,
          applicableAreas,
          documents,
        });
        if (!res.success) {
          setReqErrors({ form: res.message ?? t('admin.shared.requestFailed') });
          return;
        }
        setFlashToast({ message: t('metadata.subCategoryForm.createSuccess'), variant: 'success' });
      }
      nav('/metadata/subcategories');
      keepLocked = true;
    } finally {
      if (!keepLocked) {
        savingRef.current = false;
        setSaving(false);
      }
    }
  }

  const lookupsLoading = lookupsState === 'loading';
  const allDocumentsSelected =
    documentTypes.length > 0 && documentTypes.every((d) => selectedDocumentTypeIds.includes(d.id));

  return (
    <>
      <div className="bc">
        <span>{t('nav.groups.metadata')}</span>
        <span className="bc-sep">/</span>
        <Link to="/metadata/subcategories">{t('metadata.subCategories.title')}</Link>
        <span className="bc-sep">/</span>
        {isEdit ? t('metadata.subCategoryForm.updateTitle') : t('metadata.subCategoryForm.addTitle')}
      </div>

      <div className="pg-title">{isEdit ? t('metadata.subCategoryForm.updateTitle') : t('metadata.subCategoryForm.addTitle')}</div>

      <div className="cr-table-wrap" style={{ overflow: 'visible' }}>
        <div style={{ padding: 18 }}>
          {loading ? <div style={{ color: 'var(--t2)' }}>{t('common.loading')}</div> : null}
          {reqErrors.form ? <div className="si-err" style={{ marginBottom: 12 }}>{reqErrors.form}</div> : null}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <IdFieldSelect
              id="subCategory-category"
              label={t('admin.shared.fileCategory')}
              required={!isEdit}
              value={categoryId}
              placeholder={lookupsLoading ? t('common.loading') : t('metadata.subCategories.selectFileCategory')}
              options={fileCategories.map((x) => ({ id: x.id, label: x.name }))}
              error={reqErrors.categoryId}
              disabled={lookupsLoading || loading || isEdit}
              onChange={(v) => {
                setCategoryId(v);
                clearReqError('categoryId');
              }}
            />

            <div className="al-f">
              <div className="al-l">
                {t('profile.name')} {!isEdit ? <span className="req">*</span> : null}
              </div>
              <input
                id="subCategory-name"
                className="al-inp"
                value={name}
                placeholder={t('metadata.subCategoryForm.namePlaceholder')}
                readOnly={isEdit}
                disabled={isEdit || loading}
                onChange={(e) => {
                  setName(e.target.value);
                  clearReqError('name');
                }}
                style={{
                  ...(reqErrors.name ? { borderColor: 'var(--red)' } : {}),
                  ...(isEdit ? READONLY_FIELD_STYLE : {}),
                }}
                aria-invalid={reqErrors.name ? true : undefined}
              />
              {reqErrors.name ? (
                <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 6, fontWeight: 600 }}>{reqErrors.name}</div>
              ) : null}
            </div>

            {!isEdit ? (
              <>
                <div className="al-f">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, userSelect: 'none', marginTop: 28 }}>
                    <input
                      type="checkbox"
                      checked={bindName}
                      disabled={loading}
                      onChange={(e) => {
                        setBindName(e.target.checked);
                      }}
                    />
                    <span>{t('metadata.subCategoryForm.bindName')}</span>
                  </label>
                </div>
                <div aria-hidden="true" />
              </>
            ) : null}

            <div className="al-f">
              <div className="al-l">{t('admin.shared.code')}</div>
              <input
                id="subCategory-code"
                className="al-inp"
                value={code}
                placeholder={t('metadata.subCategoryForm.codePlaceholder')}
                readOnly
                disabled={loading}
                style={{
                  ...(reqErrors.code ? { borderColor: 'var(--red)' } : {}),
                  ...READONLY_FIELD_STYLE,
                }}
                aria-invalid={reqErrors.code ? true : undefined}
              />
              {reqErrors.code ? (
                <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 6, fontWeight: 600 }}>{reqErrors.code}</div>
              ) : null}
            </div>

            <div className="al-f">
              <div className="al-l" aria-hidden="true" style={{ visibility: 'hidden' }}>
                {t('admin.shared.code')}
              </div>
              <label className="ccr-chk" style={{ marginBottom: 0 }}>
                <input
                  type="checkbox"
                  checked={active}
                  disabled={loading}
                  onChange={(e) => setActive(e.target.checked)}
                />
                <span>{t('common.active')}</span>
              </label>
            </div>

            <div className="al-f" style={{ gridColumn: '1 / -1' }}>
              <div className="al-l">{t('metadata.subCategoryForm.applicableArea')}</div>
              <div
                className="intranet-role-rows"
                id="subCategory-applicableAreas"
                style={{ width: '50%', maxWidth: 'calc(50% - 7px)' }}
              >
                {applicableAreaRows.map((row, index) => {
                  const isDuplicate = duplicateApplicableAreaIndices.has(index);
                  return (
                  <div key={index} className="intranet-role-picker-row">
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <input
                        className="al-inp"
                        style={{
                          width: '100%',
                          ...(isDuplicate ? { borderColor: 'var(--red)' } : {}),
                        }}
                        value={row}
                        placeholder={t('metadata.subCategoryForm.applicableAreaItemPlaceholder')}
                        disabled={loading}
                        aria-invalid={isDuplicate ? true : undefined}
                        onChange={(e) => {
                          const next = [...applicableAreaRows];
                          next[index] = e.target.value;
                          setApplicableAreaRows(next);
                          clearReqError('applicableAreas');
                        }}
                      />
                      {isDuplicate ? (
                        <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 6, fontWeight: 600 }}>
                          {t('metadata.subCategoryForm.applicableAreaDuplicateError')}
                        </div>
                      ) : null}
                    </div>
                    {applicableAreaRows.length > 1 ? (
                      <button
                        type="button"
                        className="intranet-role-add-btn"
                        aria-label={t('metadata.subCategoryForm.removeApplicableArea')}
                        title={t('metadata.subCategoryForm.removeApplicableArea')}
                        disabled={loading}
                        onClick={() => {
                          setApplicableAreaRows((cur) => cur.filter((_, i) => i !== index));
                          clearReqError('applicableAreas');
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                          <line x1="5" y1="12" x2="19" y2="12" />
                        </svg>
                      </button>
                    ) : null}
                    {index === applicableAreaRows.length - 1 ? (
                      <button
                        type="button"
                        className="intranet-role-add-btn"
                        aria-label={t('metadata.subCategoryForm.addApplicableArea')}
                        title={
                          canAddApplicableArea
                            ? t('metadata.subCategoryForm.addApplicableArea')
                            : t('metadata.subCategoryForm.applicableAreaEmptyAddBlocked')
                        }
                        disabled={loading || !canAddApplicableArea}
                        onClick={() => {
                          setApplicableAreaRows((cur) => [...cur, '']);
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                          <line x1="12" y1="5" x2="12" y2="19" />
                          <line x1="5" y1="12" x2="19" y2="12" />
                        </svg>
                      </button>
                    ) : null}
                  </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div style={{ height: 16 }} />

          <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--t2)', marginBottom: 8 }}>
            {t('metadata.subCategoryForm.documentTypes')}{' '}
            <span style={{ color: 'var(--t3)', fontWeight: 700 }}>
              ({t('admin.shared.selectedCount', { count: selectedDocumentTypeIds.length })})
            </span>
          </div>

          <div
            id="subCategory-documentTypes"
            style={{
              border: '1px solid var(--border)',
              borderRadius: 'var(--r)',
              overflow: 'hidden',
              width: 'fit-content',
              maxWidth: '100%',
            }}
          >
            <div style={{ maxHeight: 280, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr
                  style={{
                    background: 'color-mix(in srgb, var(--bg) 55%, var(--surface))',
                    fontWeight: 700,
                    fontSize: 12,
                  }}
                >
                  <th
                    style={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                      background: 'color-mix(in srgb, var(--bg) 55%, var(--surface))',
                      width: 32,
                      padding: '10px 0 10px 12px',
                      textAlign: 'left',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={allDocumentsSelected}
                      disabled={loading || documentTypes.length === 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedDocumentTypeIds(documentTypes.map((d) => d.id));
                        } else {
                          setSelectedDocumentTypeIds([]);
                          setDocumentOptionalById({});
                        }
                      }}
                    />
                  </th>
                  <th
                    style={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                      background: 'color-mix(in srgb, var(--bg) 55%, var(--surface))',
                      padding: '10px 12px 10px 8px',
                      textAlign: 'left',
                      fontWeight: 700,
                    }}
                  >
                    {t('metadata.subCategoryForm.selectAllDocumentTypes')}
                  </th>
                  <th
                    style={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                      background: 'color-mix(in srgb, var(--bg) 55%, var(--surface))',
                      padding: '10px 12px 10px 16px',
                      textAlign: 'center',
                      fontWeight: 700,
                      color: 'var(--t3)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {t('metadata.subCategoryForm.documentOptional')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {lookupsLoading ? (
                  <tr>
                    <td colSpan={3} style={{ padding: 12, color: 'var(--t2)', fontSize: 12 }}>
                      {t('common.loading')}
                    </td>
                  </tr>
                ) : documentTypes.length === 0 ? (
                  <tr>
                    <td colSpan={3} style={{ padding: 12, color: 'var(--t2)', fontSize: 12 }}>
                      {t('common.noDataFound')}
                    </td>
                  </tr>
                ) : (
                  documentTypes.map((doc) => {
                    const checked = selectedDocumentTypeIds.includes(doc.id);
                    const optional = documentOptionalById[doc.id] ?? false;
                    return (
                      <tr
                        key={doc.id}
                        style={{
                          borderTop: '1px solid var(--bl)',
                          opacity: loading ? 0.6 : 1,
                        }}
                      >
                        <td style={{ width: 32, padding: '10px 0 10px 12px', verticalAlign: 'top' }}>
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={loading}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedDocumentTypeIds((cur) => [...cur, doc.id]);
                                return;
                              }
                              setSelectedDocumentTypeIds((cur) => cur.filter((id) => id !== doc.id));
                              setDocumentOptionalById((cur) => {
                                if (!(doc.id in cur)) return cur;
                                const next = { ...cur };
                                delete next[doc.id];
                                return next;
                              });
                            }}
                          />
                        </td>
                        <td style={{ padding: '10px 12px 10px 8px', verticalAlign: 'top' }}>
                          <span style={{ fontWeight: 600 }}>{doc.name}</span>
                          {doc.description ? (
                            <span style={{ display: 'block', color: 'var(--t2)', fontSize: 11, marginTop: 2 }}>
                              {doc.description}
                            </span>
                          ) : null}
                        </td>
                        <td style={{ padding: '10px 12px 10px 16px', verticalAlign: 'top', textAlign: 'center' }}>
                          <label
                            style={{
                              display: 'inline-flex',
                              cursor: loading || !checked ? 'not-allowed' : 'pointer',
                              opacity: checked ? 1 : 0.45,
                            }}
                            title={t('metadata.subCategoryForm.documentOptionalHint')}
                          >
                            <input
                              type="checkbox"
                              checked={optional}
                              disabled={loading || !checked}
                              aria-label={`${doc.name} — ${t('metadata.subCategoryForm.documentOptional')}`}
                              onChange={(e) => {
                                setDocumentOptionalById((cur) => ({ ...cur, [doc.id]: e.target.checked }));
                              }}
                            />
                          </label>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
            </div>
          </div>
        </div>

        <div className="tft" style={{ background: 'color-mix(in srgb, var(--bg) 60%, var(--surface))' }}>
          <div />
          <div className="pgr">
            <button className="mfb" type="button" disabled={saving} onClick={() => setCancelConfirmOpen(true)}>
              {t('common.cancel')}
            </button>
            <Button
              variant="primary"
              onClick={() => {
                void handleSave();
              }}
              disabled={saving || loading}
            >
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={cancelConfirmOpen}
        title={t('admin.shared.cancelChanges')}
        message={t('admin.shared.cancelChangesMessage')}
        onClose={() => setCancelConfirmOpen(false)}
        onConfirm={() => {
          setCancelConfirmOpen(false);
          nav('/metadata/subcategories');
        }}
      />
    </>
  );
}
